import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, CheckCircle2, ChevronsUpDown, Download, Loader2, PackageMinus, Printer, RotateCcw, Save, ScanLine, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { downloadCsv, escapeHtml, openPrintableHtml } from '@/lib/wms-document-output';
import { usePageParams } from '@/hooks/usePageParams';

type IssueLineMeta = {
  manualInventoryId?: string;
  manualInventoryCode?: string;
  cutIssueInventoryId?: string;
  cutBalanceInventoryId?: string;
  stockNo?: string;
  heatNo?: string;
  millCert?: string;
  countryOfOrigin?: string;
  documentUpload?: string;
  location?: string;
  checkedRef?: string;
  returnQty?: string;
  sourceRequestLengthMm?: string | number;
  sourceRequestLength?: string | number;
  requestLength?: string | number;
  length_mm?: string | number;
  length?: string;
  returnLength?: string;
  irtNo?: string;
  remark?: string;
};

type InspectionOverrideMeta = {
  allowed?: boolean;
  reason?: string;
  authorizedBy?: string | null;
  authorizedRole?: string | null;
  authorizedAt?: string | null;
};

type PickingDetail = {
  id: string;
  source?: 'PICK_LIST' | 'ALLOCATION';
  pick_list_id?: string | null;
  pick_list_number?: string | null;
  sequence_number?: number | null;
  inventory_id?: string | null;
  bin_id?: string | null;
  bin_code?: string | null;
  location?: string | null;
  qty_to_pick?: number;
  qty_picked?: number;
  status?: string | null;
  remark?: string | null;
};

type IssueOrderLine = {
  id: string;
  line_number: number;
  sku_code: string | null;
  sku_name: string | null;
  qty_ordered: number;
  planned_issue_qty: number;
  cut_required: boolean;
  cut_qty: number;
  direct_issue_qty: number;
  balance_qty: number;
  returnable_qty: number;
  returned_qty: number;
  issue_remark: string | null;
  lot_number: string | null;
  status: string;
  metadata?: Record<string, unknown> | null;
  picking_details?: PickingDetail[];
};

type IssueOrderDetail = {
  id: string;
  order_number: string;
  stock_request_number: string | null;
  project_code: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  status: string;
  required_date: string | null;
  planned_issue_qty_total: number;
  issue_length_mm_total?: number;
  returnable_qty_total: number;
  returned_qty_total: number;
  return_pending_qty: number;
  notes?: string | null;
  metadata?: {
    inspectionOverride?: InspectionOverrideMeta;
  } | null;
  lines: IssueOrderLine[];
};

type WmsIssueOrderDetailPanelProps = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
  returnModePreset?: ReturnMode;
};

type HeaderDraft = {
  requiredDate: string;
  notes: string;
};

type ReturnMode = 'SINGLE_MATERIAL' | 'PROJECT_CLOSE_BALANCE';

type LineDraft = {
  plannedIssueQty: string;
  cutRequired: boolean;
  cutQty: string;
  directIssueQty: string;
  inventoryId: string;
  inventoryCode: string;
  stockNo: string;
  heatNo: string;
  millCert: string;
  countryOfOrigin: string;
  documentUpload: string;
  location: string;
  checkedRef: string;
  returnQty: string;
  returnSelected: boolean;
  returnLength: string;
  irtNo: string;
  issueRemark: string;
};

function buildHeaderDraft(order: IssueOrderDetail): HeaderDraft {
  return {
    requiredDate: formatDate(order.required_date) === '-' ? '' : formatDate(order.required_date),
    notes: order.notes ?? '',
  };
}

function buildDraft(line: IssueOrderLine): LineDraft {
  const meta = (line.metadata ?? {}) as IssueLineMeta;
  return {
    plannedIssueQty: String(line.planned_issue_qty ?? line.qty_ordered ?? 0),
    cutRequired: Boolean(line.cut_required),
    cutQty: String(line.cut_qty ?? 0),
    directIssueQty: String(line.direct_issue_qty ?? line.planned_issue_qty ?? line.qty_ordered ?? 0),
    inventoryId: String(meta.cutIssueInventoryId ?? meta.manualInventoryId ?? ''),
    inventoryCode: String(meta.manualInventoryCode ?? ''),
    stockNo: String(meta.stockNo ?? ''),
    heatNo: String(meta.heatNo ?? line.lot_number ?? ''),
    millCert: String(meta.millCert ?? 'NO'),
    countryOfOrigin: String(meta.countryOfOrigin ?? ''),
    documentUpload: String(meta.documentUpload ?? ''),
    location: String(meta.location ?? ''),
    checkedRef: String(meta.checkedRef ?? 'NO'),
    returnQty: String(meta.returnQty ?? ''),
    returnSelected: false,
    returnLength: String(
      meta.sourceRequestLengthMm
        ?? meta.sourceRequestLength
        ?? meta.requestLength
        ?? meta.returnLength
        ?? meta.length_mm
        ?? meta.length
        ?? '',
    ),
    irtNo: String(meta.irtNo ?? ''),
    issueRemark: String(meta.remark ?? line.issue_remark ?? ''),
  };
}

type InventoryOption = {
  id: string;
  inventory_code?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  bin_code?: string | null;
  location?: string | null;
  loc_no?: string | null;
  default_rack?: string | null;
  qty_available?: number | null;
  lot_number?: string | null;
  heat_no?: string | null;
  mill_cert_no?: string | null;
  country_of_origin?: string | null;
  document_upload?: string | null;
  attachment_count?: number | null;
};

function inventoryOptionLocation(inventory: InventoryOption) {
  return inventory.location ?? inventory.bin_code ?? inventory.loc_no ?? inventory.default_rack ?? '';
}

function IssueInventoryPicker({
  order,
  line,
  draft,
  onSelect,
}: {
  order: IssueOrderDetail & Record<string, any>;
  line: IssueOrderLine & Record<string, any>;
  draft: LineDraft;
  onSelect: (inventory: InventoryOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<InventoryOption[]>([]);

  useEffect(() => {
    if (!open || !line.sku_code) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ includeZero: 'false', limit: '40' });
    if (order.warehouse_id) params.set('warehouseId', String(order.warehouse_id));
    if (line.sku_id) params.set('skuId', String(line.sku_id));
    if (order.project_id) params.set('projectId', String(order.project_id));
    if (query.trim()) params.set('search', query.trim());
    void api.get<any>(`/wms/inventory?${params.toString()}`)
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response) ? response : (response?.data ?? []);
        const needed = Number(draft.plannedIssueQty || 0);
        setOptions(rows.filter((row: InventoryOption) => Number(row.qty_available ?? 0) >= needed));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [draft.plannedIssueQty, line.sku_code, line.sku_id, open, order.project_id, order.warehouse_id, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <span className="truncate text-left">{draft.inventoryCode || 'Pick inventory code'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] max-w-[90vw] p-0">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search inventory code or location..." />
          <CommandList className="max-h-[320px]">
            {loading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading inventory...
              </div>
            ) : null}
            {!loading ? <CommandEmpty>No matching inventory found.</CommandEmpty> : null}
            <CommandGroup>
              {options.map((inventory) => (
                <CommandItem
                  key={inventory.id}
                  value={`${inventory.inventory_code ?? inventory.id} ${inventoryOptionLocation(inventory)}`}
                  onSelect={() => {
                    onSelect(inventory);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', draft.inventoryId === inventory.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{inventory.inventory_code ?? inventory.id}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {(inventoryOptionLocation(inventory) || '-') + ` · Available ${Number(inventory.qty_available ?? 0).toLocaleString()} mm`}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function statusTone(status: string) {
  switch (status) {
    case 'SHIPPED':
      return 'bg-green-100 text-green-700';
    case 'ALLOCATED':
      return 'bg-blue-100 text-blue-700';
    case 'PICKING':
      return 'bg-yellow-100 text-yellow-700';
    case 'PACKED':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function nextActionHint(order: IssueOrderDetail) {
  if (order.status === 'NEW') {
    return 'Next: Select inventory code, then click Start Picking. Cutting is applied automatically when the issue is completed.';
  }
  if (order.status === 'ALLOCATED') {
    return 'Next: Click Start Picking.';
  }
  if (order.status === 'PICKING' || order.status === 'PACKED') {
    return 'Next: Click Complete.';
  }
  if (order.status === 'SHIPPED') {
    return 'Issue completed. Use Open Material Return only if stock is returned from site.';
  }
  return null;
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

function WmsIssueOrderDetailContent({
  id: idProp,
  embedded = false,
  onClose,
  returnModePreset,
}: WmsIssueOrderDetailPanelProps) {
  const navigate = useNavigate();
  const params = usePageParams<{ id?: string }>();
  const id = idProp ?? params.id;
  const [order, setOrder] = useState<IssueOrderDetail | null>(null);
  const [headerDraft, setHeaderDraft] = useState<HeaderDraft | null>(null);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [inlineWarning, setInlineWarning] = useState('');
  const [allowInspectionOverride, setAllowInspectionOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await api.get<IssueOrderDetail>(`/wms/issue-orders/${id}`);
      setOrder(detail);
      setHeaderDraft(buildHeaderDraft(detail));
      setDrafts(Object.fromEntries(detail.lines.map((line) => [line.id, buildDraft(line)])));
      setInlineWarning('');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load issue order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  const issuePlanLocked = order?.status === 'SHIPPED' || order?.status === 'CANCELLED';
  const actionHint = order ? nextActionHint(order) : null;

  const closeOrBack = () => {
    if (embedded) {
      onClose?.();
      return;
    }
    navigate('/wms/issue-orders');
  };

  const openReturnFormWorkflow = () => {
    if (!id) return;
    navigate(`/wp/returns/form?createIssueOrderId=${encodeURIComponent(id)}`);
  };

  const openProjectTransferWorkflow = () => {
    navigate('/wp/returns/project');
  };

  const patchDraft = (line: IssueOrderLine, next: Partial<LineDraft>) => {
    setInlineWarning('');
    setDrafts((prev) => {
      const current = prev[line.id] ?? buildDraft(line);
      const merged: LineDraft = { ...current, ...next };
      const planned = Number(merged.plannedIssueQty || 0);
      if (merged.cutRequired) {
        merged.cutQty = String(planned);
        merged.directIssueQty = '0';
      } else {
        merged.cutQty = '0';
        merged.directIssueQty = String(planned);
      }
      return { ...prev, [line.id]: merged };
    });
  };

  const buildLineSavePayload = (line: IssueOrderLine) => {
    const draft = drafts[line.id] ?? buildDraft(line);
    const metadata = {
      ...(line.metadata ?? {}),
      manualInventoryId: draft.inventoryId || null,
      manualInventoryCode: draft.inventoryCode || null,
      stockNo: draft.stockNo,
      heatNo: draft.heatNo,
      millCert: draft.millCert,
      countryOfOrigin: draft.countryOfOrigin,
      documentUpload: draft.documentUpload,
      location: draft.location,
      checkedRef: draft.checkedRef,
      returnQty: draft.returnQty,
      irtNo: draft.irtNo,
      remark: draft.issueRemark,
    };
    return {
      ...(issuePlanLocked ? {} : {
        plannedIssueQty: Number(draft.plannedIssueQty || 0),
        cutRequired: true,
        cutQty: Number(draft.plannedIssueQty || 0),
        directIssueQty: 0,
      }),
      issueRemark: draft.issueRemark,
      metadata,
    };
  };

  const persistLineDraftForAction = async (line: IssueOrderLine) => {
    if (!id) return;
    await api.patch(`/wms/issue-orders/${id}/lines/${line.id}`, buildLineSavePayload(line));
  };

  const persistHeaderDraftForAction = async () => {
    if (!id || !headerDraft) return;
    await api.patch(`/wms/outbound/${id}`, {
      requiredDate: headerDraft.requiredDate || undefined,
      notes: headerDraft.notes || undefined,
    });
  };

  const saveAll = async () => {
    if (!id || !order) return;
    setSavingAll(true);
    try {
      setInlineWarning('');
      await persistHeaderDraftForAction();
      for (const line of order.lines) {
        await persistLineDraftForAction(line);
      }
      toast.success('Issue order saved');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save issue order');
    } finally {
      setSavingAll(false);
    }
  };

  const runOrderAction = async (actionKey: 'cutting' | 'allocate' | 'pick' | 'ship') => {
    if (!id || !order) return;
    if (actionKey === 'allocate' || actionKey === 'pick') {
      const missingInventoryLines = order.lines.filter((line) => {
        const draft = drafts[line.id] ?? buildDraft(line);
        const plannedQty = Number(draft.plannedIssueQty || line.planned_issue_qty || line.qty_ordered || 0);
        return plannedQty > 0 && !String(draft.inventoryId || '').trim();
      });
      if (missingInventoryLines.length > 0) {
        const lineLabels = missingInventoryLines.map((line) => `Line ${line.line_number}`).join(', ');
        setInlineWarning(`${lineLabels} must select Inventory Code before Start Picking.`);
        return;
      }
    }
    const confirmed = (
      actionKey === 'ship'
        ? window.confirm('Confirm goods issue completed?')
        : actionKey === 'cutting'
          ? window.confirm('Run cutting for cut-required lines?')
          : true
    );
    if (!confirmed) return;

    setRunningAction(actionKey);
    try {
      setInlineWarning('');
      if (actionKey === 'allocate' || actionKey === 'pick' || actionKey === 'ship') {
        await persistHeaderDraftForAction();
        for (const line of order.lines) {
          await persistLineDraftForAction(line);
        }
      }
      if (actionKey === 'cutting') {
        await api.post(`/wms/issue-orders/${id}/run-cutting`, {});
      } else if (actionKey === 'allocate') {
        await api.post(`/wms/outbound/${id}/allocate`, {
          allowInspectionOverride,
          overrideReason: allowInspectionOverride ? overrideReason.trim() : undefined,
        });
        await api.patch(`/wms/issue-orders/${id}/status`, { status: 'PICKING' });
      } else if (actionKey === 'pick') {
        await api.patch(`/wms/issue-orders/${id}/status`, { status: 'PICKING' });
      } else if (actionKey === 'ship') {
        const hasCutLine = order.lines.some((line) => {
          const draft = drafts[line.id] ?? buildDraft(line);
          return draft.cutRequired;
        });
        if (hasCutLine) {
          await api.post(`/wms/issue-orders/${id}/run-cutting`, {});
        }
        await api.post(`/wms/outbound/${id}/ship`, {});
      }
      toast.success('Action completed');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Action failed');
    } finally {
      setRunningAction(null);
    }
  };

  const handleExportCsv = () => {
    if (!order) return;
    const columns = [
      { key: 'issueNo', label: 'Issue No' },
      { key: 'requestRef', label: 'Request No' },
      { key: 'project', label: 'Project' },
      { key: 'requestDate', label: 'Request Date' },
      { key: 'status', label: 'Status' },
      { key: 'lineNo', label: 'Line' },
      { key: 'materialCode', label: 'Material Code' },
      { key: 'materialSpec', label: 'Material / Spec' },
      { key: 'qtyIssued', label: 'Qty Issued' },
      { key: 'directIssueQty', label: 'Direct Issue Qty' },
      { key: 'stockNo', label: 'Stock No' },
      { key: 'heatNo', label: 'Heat No' },
      { key: 'millCert', label: 'Mill Cert' },
      { key: 'countryOfOrigin', label: 'Country Of Origin' },
      { key: 'documentUpload', label: 'Document Attachment' },
      { key: 'location', label: 'Location' },
      { key: 'checkedRef', label: 'Checked Ref' },
      { key: 'returnQty', label: 'Return Qty' },
      { key: 'irtNo', label: 'IRT No' },
      { key: 'remark', label: 'Remark' },
    ];
    const rows = order.lines.map((line) => {
      const draft = drafts[line.id] ?? buildDraft(line);
      return {
        issueNo: order.order_number,
        requestRef: order.stock_request_number ?? '-',
        project: order.project_code ?? '-',
        requestDate: formatDate(order.required_date),
        status: order.status,
        lineNo: line.line_number,
        materialCode: line.sku_code ?? '-',
        materialSpec: line.sku_name ?? '-',
        qtyIssued: draft.plannedIssueQty,
        directIssueQty: draft.directIssueQty,
        stockNo: draft.stockNo,
        heatNo: draft.heatNo,
        millCert: draft.millCert,
        countryOfOrigin: draft.countryOfOrigin,
        documentUpload: draft.documentUpload,
        location: draft.location,
        checkedRef: draft.checkedRef,
        returnQty: draft.returnQty,
        irtNo: draft.irtNo,
        remark: draft.issueRemark,
      };
    });
    downloadCsv(`${order.order_number}-issue-slip.csv`, columns, rows);
  };

  const handlePrintSlip = () => {
    if (!order) return;
    const rowsHtml = order.lines.map((line) => {
      const draft = drafts[line.id] ?? buildDraft(line);
      const cells = [
        line.line_number,
        line.sku_code ?? '-',
        line.sku_name ?? '-',
        draft.plannedIssueQty,
        draft.directIssueQty,
        draft.stockNo || '-',
        draft.heatNo || '-',
        draft.millCert || '-',
        draft.countryOfOrigin || '-',
        draft.documentUpload || '-',
        draft.location || '-',
        draft.checkedRef || '-',
        draft.returnQty || '-',
        draft.irtNo || '-',
        draft.issueRemark || '-',
      ];
      return `<tr>${cells.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('')}</tr>`;
    }).join('');
    openPrintableHtml({
      title: `${order.order_number} Issue / Return Slip`,
      landscape: true,
      variant: 'legacy',
      bodyHtml: `
        <div class="legacy-doc">
          <div class="legacy-title">
            <h1>Internal Goods Issue and Material Return</h1>
            <p>Wasco Issue / Return Slip Layout</p>
          </div>
          <table class="legacy-meta-table">
            <tbody>
              <tr>
                <th>Issue No</th><td>${escapeHtml(order.order_number)}</td>
                <th>Request No</th><td>${escapeHtml(order.stock_request_number ?? '-')}</td>
                <th>Project</th><td>${escapeHtml(order.project_code ?? '-')}</td>
                <th>Status</th><td>${escapeHtml(order.status)}</td>
              </tr>
              <tr>
                <th>Request Date</th><td>${escapeHtml(formatDate(order.required_date))}</td>
                <th>Issued Qty</th><td>${escapeHtml(String(Number(order.planned_issue_qty_total ?? 0)))}</td>
                <th>Returned Qty</th><td>${escapeHtml(String(Number(order.returned_qty_total ?? 0)))}</td>
                <th>Return Balance</th><td>${escapeHtml(String(Number(order.return_pending_qty ?? 0)))}</td>
              </tr>
            </tbody>
          </table>
          <div>
            <h2 class="section-title">Issue / Return Lines</h2>
            <table class="legacy-line-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Material Code</th>
                  <th>Material / Spec</th>
                  <th>Qty Issued</th>
                  <th>Direct Issue Qty</th>
                  <th>Stock No</th>
                  <th>Heat No</th>
                  <th>Mill Cert</th>
                  <th>Country Of Origin</th>
                  <th>Document Attachment</th>
                  <th>Location</th>
                  <th>Checked Ref</th>
                  <th>Return Qty</th>
                  <th>IRT No</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
          <div class="legacy-signoff">
            <div>Issued By</div>
            <div>Received By</div>
            <div>Checked By</div>
          </div>
        </div>`,
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-4 p-4">
        <Button variant="outline" size="sm" onClick={closeOrBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {embedded ? 'Close' : 'Back to Issue Orders'}
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Issue order not found
          </CardContent>
        </Card>
      </div>
    );
  }

  if (returnModePreset) {
    const title = returnModePreset === 'PROJECT_CLOSE_BALANCE' ? 'Inventory Transfer' : 'Material Return';
    const openWorkflow = returnModePreset === 'PROJECT_CLOSE_BALANCE' ? openProjectTransferWorkflow : openReturnFormWorkflow;
    return (
      <div className={embedded ? 'space-y-4 pb-4' : 'space-y-4 p-4'}>
        <div className="space-y-3">
          <div className="space-y-1">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={closeOrBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {embedded ? 'Close' : `Back to ${title}`}
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{order.order_number}</h1>
              <Badge variant="outline" className={statusTone(order.status)}>{order.status}</Badge>
              {order.project_code ? <Badge variant="outline">{order.project_code}</Badge> : null}
              {order.stock_request_number ? <Badge variant="outline">Req {order.stock_request_number}</Badge> : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-2">
          <Button onClick={openWorkflow}>
            <RotateCcw className="mr-2 h-4 w-4" />
              {returnModePreset === 'PROJECT_CLOSE_BALANCE' ? 'Open Inventory Transfer' : 'Open Material Return'}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Issue No</Label>
                <Input className="mt-1" value={order.order_number} disabled />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Request No</Label>
                <Input className="mt-1" value={order.stock_request_number ?? '-'} disabled />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Source Project</Label>
                <Input className="mt-1" value={order.project_code ?? '-'} disabled />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Return Balance</Label>
                <Input className="mt-1" value={String(order.return_pending_qty ?? 0)} disabled />
              </div>
            </div>
            <div className="rounded-md border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              {returnModePreset === 'PROJECT_CLOSE_BALANCE'
                ? 'Inventory Transfer is now handled by transfer request workflow. Use the dedicated Inventory Transfer page for draft, approval, and execution.'
                : 'Material Return now owns the draft, submit, approve flow. This issue order no longer creates Inventory Return directly.'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4 pb-4' : 'space-y-4 p-4'}>
      <div className="space-y-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={closeOrBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {embedded ? 'Close' : 'Back to Issue Orders'}
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{order.order_number}</h1>
            <Badge variant="outline" className={statusTone(order.status)}>{order.status}</Badge>
            {order.project_code ? <Badge variant="outline">{order.project_code}</Badge> : null}
            {order.stock_request_number ? <Badge variant="outline">Req {order.stock_request_number}</Badge> : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-start gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintSlip}>
            <Printer className="mr-2 h-4 w-4" />
            Print Slip
          </Button>
          {order.status !== 'SHIPPED' && order.status !== 'CANCELLED' ? (
            <Button variant="outline" size="sm" onClick={() => void saveAll()} disabled={savingAll || runningAction !== null || !headerDraft}>
              {savingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save All
            </Button>
          ) : null}
          {order.status === 'NEW' ? (
            <Button size="sm" onClick={() => void runOrderAction('allocate')} disabled={runningAction !== null || savingAll}>
              {runningAction === 'allocate' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageMinus className="mr-2 h-4 w-4" />}
              Start Picking
            </Button>
          ) : null}
          {order.status === 'ALLOCATED' ? (
            <Button size="sm" onClick={() => void runOrderAction('pick')} disabled={runningAction !== null || savingAll}>
              {runningAction === 'pick' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanLine className="mr-2 h-4 w-4" />}
              Start Picking
            </Button>
          ) : null}
          {(order.status === 'PICKING' || order.status === 'PACKED') ? (
            <Button size="sm" onClick={() => void runOrderAction('ship')} disabled={runningAction !== null || savingAll}>
              {runningAction === 'ship' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Complete
            </Button>
          ) : null}
          {order.status === 'SHIPPED' ? (
            <Button variant="outline" size="sm" onClick={openReturnFormWorkflow}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Open Material Return
            </Button>
          ) : null}
        </div>
      </div>

      {inlineWarning ? (
        <div className="mx-auto w-full max-w-4xl">
          <div className="flex items-start justify-between gap-3 rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-900 shadow-sm">
            <div>{inlineWarning}</div>
            <button
              type="button"
              className="shrink-0 rounded p-1 text-sky-700 hover:bg-sky-100"
              onClick={() => setInlineWarning('')}
              aria-label="Close warning"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      {actionHint ? (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {actionHint}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Issue / Return Slip Header</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Issue No</Label>
            <Input className="mt-1" value={order.order_number} disabled />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Request No</Label>
            <Input className="mt-1" value={order.stock_request_number ?? ''} disabled />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Request Date</Label>
            <Input
              className="mt-1"
              type="date"
              value={headerDraft?.requiredDate ?? ''}
              onChange={(event) => setHeaderDraft((prev) => ({ ...(prev ?? buildHeaderDraft(order)), requiredDate: event.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Project</Label>
            <Input className="mt-1" value={order.project_code ?? ''} disabled />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
            <Input className="mt-1" value={order.status} disabled />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Issued / Returned Qty</Label>
            <Input
              className="mt-1"
              value={`${Number(order.planned_issue_qty_total ?? 0).toLocaleString()} / ${Number(order.returned_qty_total ?? 0).toLocaleString()}`}
              disabled
            />
          </div>
          <div className="md:col-span-2 xl:col-span-6">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Header Note</Label>
            <Textarea
              className="mt-1"
              rows={2}
              value={headerDraft?.notes ?? ''}
              onChange={(event) => setHeaderDraft((prev) => ({ ...(prev ?? buildHeaderDraft(order)), notes: event.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Outbound Lines</CardTitle>
            {issuePlanLocked ? (
              <Badge variant="outline" className="bg-slate-100 text-slate-700">
                Outbound completed; lines locked
              </Badge>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <div className="runtime-grid-scrollbar boox-x-scroll overflow-auto rounded-lg border">
            <table className="min-w-[1460px] w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background">
                <tr className="border-b bg-slate-50">
                  <th className="px-3 py-2 text-left">Line</th>
                  <th className="px-3 py-2 text-left">Inventory Code</th>
                  <th className="px-3 py-2 text-left">Material</th>
                  <th className="px-3 py-2 text-left">Length (mm)</th>
                  <th className="px-3 py-2 text-left">Heat No</th>
                  <th className="px-3 py-2 text-left">Mill Cert No</th>
                  <th className="px-3 py-2 text-left">Country Of Origin</th>
                  <th className="px-3 py-2 text-left">Document Attachment</th>
                  <th className="px-3 py-2 text-right">Issue Qty (pcs)</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-left">Remark</th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((line) => {
                  const draft = drafts[line.id] ?? buildDraft(line);
                  return (
                    <tr key={line.id} className="border-t align-top">
                      <td className="px-3 py-3 font-medium">{line.line_number}</td>
                      <td className="min-w-[240px] px-3 py-3">
                        <IssueInventoryPicker
                          order={order as IssueOrderDetail & Record<string, any>}
                          line={line as IssueOrderLine & Record<string, any>}
                          draft={draft}
                          onSelect={(inventory) => patchDraft(line, {
                            inventoryId: inventory.id,
                            inventoryCode: inventory.inventory_code ?? inventory.id,
                            location: inventoryOptionLocation(inventory) || draft.location,
                            heatNo: inventory.heat_no ?? draft.heatNo,
                            millCert: inventory.mill_cert_no ?? draft.millCert,
                            countryOfOrigin: inventory.country_of_origin ?? draft.countryOfOrigin,
                            documentUpload: inventory.document_upload ?? (Number(inventory.attachment_count ?? 0) > 0 ? `${Number(inventory.attachment_count ?? 0)} file(s)` : draft.documentUpload),
                          })}
                        />
                      </td>
                      <td className="min-w-[220px] px-3 py-3">
                        <div className="font-medium">{line.sku_name || '-'}</div>
                        <div className="text-xs text-muted-foreground">{line.sku_code || '-'}</div>
                      </td>
                      <td className="px-3 py-3">{draft.returnLength || '-'}</td>
                      <td className="px-3 py-3">{draft.heatNo || '-'}</td>
                      <td className="px-3 py-3">{draft.millCert || '-'}</td>
                      <td className="px-3 py-3">{draft.countryOfOrigin || '-'}</td>
                      <td className="px-3 py-3">{draft.documentUpload || '-'}</td>
                      <td className="px-3 py-3 text-right">
                        <span>{draft.plannedIssueQty || '-'}</span>
                      </td>
                      <td className="px-3 py-3"><span>{draft.location || '-'}</span></td>
                      <td className="min-w-[220px] px-3 py-3">
                        <Textarea rows={2} className="min-h-[64px]" value={draft.issueRemark} onChange={(e) => patchDraft(line, { issueRemark: e.target.value })} disabled={issuePlanLocked} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function WmsIssueOrderDetailPanel(props: WmsIssueOrderDetailPanelProps) {
  return <WmsIssueOrderDetailContent {...props} embedded={props.embedded ?? true} />;
}

export default function WmsIssueOrderDetailPage() {
  return <WmsIssueOrderDetailContent />;
}
